import { LabOrder, LabSku, LabSkuPrices } from '@/types/interfaces/lab'

export const locationSupportedBillTypes = (order: LabOrder) => {
    return order.lab_appointment.supported_bill_types || []
}

export const locationPermitsCashOrders = (order: LabOrder) => {
    // HACK: Vital is currently incorrectly telling us that Quest and Labcorp locations in NY and NJ accept cash orders.
    // They DO NOT.  So we will explicitly check for these here.
    if (order.lab_appointment.state === 'NY' || order.lab_appointment.state === 'NJ') {
        const labName = order.lab_appointment.location_name.toLowerCase()
        if (labName.includes('quest') || labName.includes('labcorp')) {
            return false
        }
    }

    return (
        locationSupportedBillTypes(order).includes('client_bill') ||
        locationSupportedBillTypes(order).includes('patient_bill_passthrough')
    )
}

export const cashBillType = (order: LabOrder) => {
    if (locationSupportedBillTypes(order).includes('client_bill')) {
        return 'client_bill'
    } else if (locationSupportedBillTypes(order).includes('patient_bill_passthrough')) {
        return 'patient_bill_passthrough'
    } else {
        return 'client_bill'
    }
}

export const insuranceBillType = (order: LabOrder) => {
    return 'commercial_insurance'
}

export const locationPermitsInsuranceOrders = (order: LabOrder) => {
    return locationSupportedBillTypes(order).includes('commercial_insurance')
}

export const customerCanUseInsurance = (order: LabOrder) => {
    return order.insurance.patient_has_active_insurance && order.insurance.insurance_permitted
}

export const orderConfiguredForInsurance = (order: LabOrder) => {
    return order.icd10_codes.length > 0
}

export const shouldSuppressMedicalNecessityForMedicationCheckIn = (
    isMedicationCheckInLabCheckout: boolean,
    hadIcd10CodesOnEntry: boolean,
) => {
    return isMedicationCheckInLabCheckout && hadIcd10CodesOnEntry
}

export const hasInsurancePrice = (order: LabOrder) => {
    return Number.isFinite(order.insurance.insurance_price)
}

/**
 * Label shown in place of an insurance price. "Copay" only reads correctly for an in-network lab;
 * at an out-of-network lab the patient may owe more than a copay.
 */
export const insuranceWithoutPriceLabel = (order: LabOrder) => {
    return order.insurance.insurance_network_status === 'out_of_network' ? 'Billed by the lab' : 'Copay'
}

export const hasCashPrice = (order: LabOrder) => {
    return Number.isFinite(order.total_price)
}

export const priceWinner = (order: LabOrder): 'insurance' | 'cash' | null => {
    if (
        locationPermitsInsuranceOrders(order) &&
        customerCanUseInsurance(order) &&
        (!locationPermitsCashOrders(order) ||
            !hasCashPrice(order) ||
            (hasInsurancePrice(order) && order.insurance.insurance_price <= order.total_price))
    ) {
        return 'insurance'
    } else if (locationPermitsCashOrders(order) && hasCashPrice(order)) {
        return 'cash'
    } else {
        return null
    }
}

/**
 * True when the patient's insurance is out-of-network at the reference labs that report a network
 * status, and in-network at none of them. Labs that report no status (e.g. Bioreference) are ignored.
 * Out-of-network labs are not quoted an insurance price, so callers should not fall back to a typical copay.
 */
export const isLabSkuInsuranceOutOfNetwork = (prices: LabSkuPrices | null | undefined): boolean => {
    const statuses = [prices?.quest, prices?.labcorp, prices?.bioreference].map((lab) => lab?.insurance_network_status)
    return statuses.includes('out_of_network') && !statuses.includes('in_network')
}

export const getPatientFacingLabBadgeName = (labSkus: LabSku[] | null): string => {
    if (!labSkus || labSkus.length === 0) {
        return ''
    }

    if (labSkus.length === 1) {
        // For single labs, check if the name is too long
        if (labSkus[0].name.length <= 40) {
            return labSkus[0].name
        }
        return '1 lab'
    }

    const detailedFormat = `${labSkus[0].name} + ${labSkus.length - 1} more`

    if (detailedFormat.length <= 40) {
        return detailedFormat
    }

    return `${labSkus.length} labs`
}
